'use client';

import { createContext, useContext, useId, type AnchorHTMLAttributes } from 'react';

import {
  NewTabHintText,
  newTabLabelling,
  type NewTabLabelling,
} from '@/components/ui/new-tab-hint-text';

/**
 * The localized "(opens in a new tab)" warning (WCAG G201). Held in context rather than read from
 * next-intl so this file stays importable by the MCP widgets, which translate through their own
 * fetched messages; with no provider there is no hint, never a raw key.
 */
const NewTabHintContext = createContext<string | null>(null);

export const NewTabHintProvider = NewTabHintContext.Provider;

export function useNewTabLabelling({
  id,
  ariaLabel,
}: {
  id?: string;
  ariaLabel?: string;
}): NewTabLabelling {
  const hint = useContext(NewTabHintContext);
  const generatedId = useId();
  return newTabLabelling({ id, ariaLabel, hint, generatedId });
}

export function NewTabHint({ id }: { id?: string }) {
  const hint = useContext(NewTabHintContext);
  if (!hint) {
    return null;
  }
  return <NewTabHintText id={id} hint={hint} />;
}

/** A link's text without the new-tab hint, so analytics record what the link itself says. */
export function readLinkText(anchor: HTMLElement): string {
  const copy = anchor.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('[data-new-tab-hint]').forEach(hint => hint.remove());
  return copy.textContent ?? '';
}

export function NewTabAnchor({
  children,
  id,
  'aria-label': ariaLabel,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { linkProps, hintId } = useNewTabLabelling({ id, ariaLabel });
  return (
    <a target="_blank" rel="noopener noreferrer" {...props} aria-label={ariaLabel} {...linkProps}>
      {children}
      <NewTabHint id={hintId} />
    </a>
  );
}
